import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AxiosError } from 'axios';
import i18n from '../i18n/i18n';
import { authService } from '../services/authService';
import { clearClientState } from '../utils/clearClientState';
import { fetchCurrentUser } from '../api/currentUserApi';
import { setTelemetryUserId } from '../services/appInsights';
import { useAppStore } from '../store/appStore';
import { runtimeEnv } from '../utils/runtimeEnv';
import type { ApiRole, GetCurrentUserResponse } from '../types/api';


/**
 * Returns true when the non-HTTP-only session hint cookie is present and its
 * embedded Unix-ms expiry timestamp is still in the future.
 *
 * A missing or expired hint means the auth cookie has definitely expired (or
 * never existed), so loadIdentity() can redirect to login without spending a
 * round-trip on GET /api/current-user.
 */
function hasValidSessionHint(): boolean {
  const name = runtimeEnv.VITE_SESSION_HINT_COOKIE;
  const pair = document.cookie.split('; ').find(c => c.startsWith(`${name}=`));
  if (!pair) return false;
  const ts = parseInt(pair.split('=')[1], 10);
  return !isNaN(ts) && Date.now() < ts;
}

interface AuthState {
  isAuthenticated: boolean;
  roles: ApiRole[];
  currentUser: GetCurrentUserResponse | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadIdentity: () => Promise<void>;
  login: (returnUrl?: string) => void;
  logout: () => Promise<void>;
  hasRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
  reset: () => void;
}

const initialState = {
  isAuthenticated: false,
  roles: [] as ApiRole[],
  currentUser: null,
  isLoading: true,
  error: null,
};

/**
 * Global authentication state store.
 * Manages user identity and provides auth-related utilities.
 *
 * isAuthenticated, roles, and currentUser are persisted to localStorage so that:
 *  - The app shell can render immediately on a cold offline start
 *    without waiting for a network round-trip.
 *  - loadIdentity can use the persisted state as a fallback when the
 *    network is unavailable rather than redirecting to the SAML IdP.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...initialState,

      /**
       * Loads the current user's profile from the API.
       * Called on app initialization and after login.
       *
       * Auth behaviour:
       *  - 401 Unauthorized means the server explicitly says not authenticated — initiate login.
       *  - Any other error is treated as a network failure, not a session expiry.
       *
       * Offline behaviour:
       *  - If the app is known-offline at call time, skip the network request
       *    entirely and rely on whatever state is persisted.
       *  - If the network request fails for any non-401 reason, fall back to
       *    persisted state instead of redirecting to the SAML IdP.
       */
      loadIdentity: async () => {
        const { isTrulyOffline } = useAppStore.getState();

        // Offline: skip network call — use persisted auth state.
        if (isTrulyOffline) {
          set({ isLoading: false });
          return;
        }

        // Pre-flight cookie check: the API sets a non-HTTP-only companion cookie
        // (cmmi-session) with the session expiry timestamp on every authenticated
        // response. If it's absent or expired we know the session is gone and can
        // redirect to login immediately without a wasted API round-trip.
        if (!hasValidSessionHint()) {
          console.warn('[Auth] Session hint cookie absent or expired — redirecting to login');
          authService.login();
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const user = await fetchCurrentUser();

          setTelemetryUserId(user.hashedUserId);
          // Adopt the server-resolved language preference on session load so it follows the
          // user to new browsers/sessions. The server already resolves an inactive/absent
          // preference to the default language, so this value is always safe to select.
          if (user.preferredLanguage) {
            useAppStore.getState().setSelectedLanguage(user.preferredLanguage);
          }
          set({
            isAuthenticated: true,
            roles: user.roles,
            currentUser: user,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          const status = (err as AxiosError)?.response?.status;

          if (status === 401) {
            // Server explicitly says unauthenticated — initiate login.
            console.warn('[Auth] User not authenticated - redirecting to login');
            authService.login();
            return;
          }

          // Network error while we believed we were online.
          // Do NOT redirect — this is not an authentication failure.
          console.warn('[Auth] Failed to load identity (network error):', err);

          const { isAuthenticated } = get();
          if (isAuthenticated) {
            // Persisted state from a previous authenticated session — continue.
            set({ isLoading: false, error: null });
          } else {
            // No persisted session and cannot reach the server.
            // Show an error without navigating away from the SPA.
            set({
              isLoading: false,
              error: i18n.t('auth.sessionUnverified'),
            });
          }
        }
      },

      /**
       * Initiates login flow (redirects to SAML or fake login).
       */
      login: (returnUrl?: string) => {
        authService.login(returnUrl);
      },

      /**
       * Initiates logout flow (redirects to SAML logout or home).
       */
      logout: async () => {
        // Clearing cached state must never prevent the redirect: a failed or blocked
        // cleanup previously left the button doing nothing at all.
        try {
          await clearClientState();
        } catch (err) {
          console.warn('[Auth] Failed to clear client state during logout:', err);
        }
        authService.logout();
      },

      /**
       * Checks if the current user has any of the specified roles.
       */
      hasRole: (requiredRoles: string[]) => {
        const { roles } = get();
        return requiredRoles.some(req =>
          roles.some(r => r.name.toLowerCase() === req.toLowerCase())
        );
      },

      /**
       * Checks if the current user has all of the specified roles.
       */
      hasAllRoles: (requiredRoles: string[]) => {
        const { roles } = get();
        return requiredRoles.every(req =>
          roles.some(r => r.name.toLowerCase() === req.toLowerCase())
        );
      },

      /**
       * Resets auth state to initial values.
       */
      reset: () => {
        setTelemetryUserId(null);
        set(initialState);
      },
    }),
    {
      name: 'cmmi-viewer-auth-state',
      // Restore the telemetry user id from persisted identity so page views fired
      // on a cold/offline start (before loadIdentity resolves) still carry the hash.
      onRehydrateStorage: () => (state) => {
        if (state?.currentUser?.hashedUserId) {
          setTelemetryUserId(state.currentUser.hashedUserId);
        }
      },
      // Only persist the identity facts — loading and error states are ephemeral.
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        roles: state.roles,
        currentUser: state.currentUser,
      }),
    }
  )
);
